import { useState, useEffect, useContext } from "react";
import { publicFetcher } from "../utils/publicFetcher";
import { AuthContext } from "../context/AuthContext";
import { ACCESS_LEVELS } from "../constants/accessLevels";
import type { AccessLevel } from "../types/common";
import type { EventData } from "../types/event";

interface OrganisationData {
  [key: string]: unknown;
}

interface UseEventDataReturn {
  data: EventData | null;
  organiser: OrganisationData | null;
  loading: boolean;
  error: string | null;
  accessLevel: AccessLevel;
}

export const useEventData = (eventId: string): UseEventDataReturn => {
  const authContext = useContext(AuthContext);
  const currentUser = authContext?.currentUser;
  const isAuthenticated = authContext?.isAuthenticated ?? false;

  const [data, setData] = useState<EventData | null>(null);
  const [organiser, setOrganiser] = useState<OrganisationData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [accessLevel, setAccessLevel] = useState<AccessLevel>(
    ACCESS_LEVELS.PUBLIC,
  );

  useEffect(() => {
    const fetchEventData = async () => {
      if (!eventId) {
        setError("errors.eventIdRequired");
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const eventResponse = await publicFetcher<EventData>({
          url: `/event/${eventId}`,
        });

        setData(eventResponse);

        // Try to fetch organiser data if event has organiser info
        if (eventResponse?.organiser?.organiser_id) {
          try {
            const organiserResponse = await publicFetcher<OrganisationData>({
              url: `/organisation/${eventResponse.organiser.organiser_id}`,
            });
            setOrganiser(organiserResponse);
          } catch (orgError) {
            console.warn("Could not fetch organiser data:", orgError);
          }
        }

        // Determine access level
        if (isAuthenticated) {
          if (currentUser?.userId === eventResponse?.organiser?.organiser_id) {
            setAccessLevel(ACCESS_LEVELS.OWN);
          } else {
            setAccessLevel(ACCESS_LEVELS.AUTHENTICATED);
          }
        } else {
          setAccessLevel(ACCESS_LEVELS.PUBLIC);
        }
      } catch (err) {
        console.error("Error fetching event data:", err);
        const errorMessage =
          err instanceof Error ? err.message : "errors.eventLoadFailed";
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchEventData();
  }, [eventId, isAuthenticated, currentUser?.userId]);

  return { data, organiser, loading, error, accessLevel };
};
